import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { CalendarDays, MapPin, Repeat, Pencil, Trash2 } from 'lucide-preact';
import {
  api,
  type EventDetail as EventDetailT,
  type Child,
  type UserBasic,
} from '../lib/api.js';

const ROLE_LABELS: Record<string, string> = {
  bring: 'Bringen',
  holt_ab: 'Abholen',
  bleibt_dabei: 'Bleibt dabei',
  kümmert_sich_komplett: 'Betreut komplett',
  kinderbetreuer: 'Kinderbetreuung',
  child_owner: 'Kind',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'ausstehend',
  accepted: 'zugesagt',
  declined: 'abgelehnt',
  auto: 'auto',
};

export function EventDetail() {
  const { params } = useRoute();
  const loc = useLocation();
  const id = params.id!;
  const [event, setEvent] = useState<EventDetailT | null>(null);
  const [users, setUsers] = useState<UserBasic[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    api<EventDetailT>(`/api/v1/events/${id}`).then(setEvent).catch((e) => setError(String(e)));
    api<UserBasic[]>('/api/v1/users').then(setUsers).catch(() => {});
    api<Child[]>('/api/v1/children').then(setChildren).catch(() => {});
  }, [id]);

  function actorName(p: EventDetailT['participants'][number]): string {
    if (p.actorType === 'external') return p.actorExternalName ?? '—';
    if (p.actorType === 'user') {
      const u = users.find((uu) => uu.id === p.actorUserId);
      return u ? u.displayName : p.actorUserId ?? '—';
    }
    if (p.actorType === 'child') {
      const c = children.find((cc) => cc.id === p.actorChildId);
      return c ? c.name : p.actorChildId ?? '—';
    }
    return '—';
  }

  function actorColor(p: EventDetailT['participants'][number]): string | null {
    if (p.actorType === 'user') return users.find((u) => u.id === p.actorUserId)?.color ?? null;
    if (p.actorType === 'child') return children.find((c) => c.id === p.actorChildId)?.color ?? null;
    return null;
  }

  async function cancel() {
    if (!confirm('Wirklich absagen? Alle verknüpften Kalender-Einträge werden entfernt.')) return;
    setCancelling(true);
    try {
      await api(`/api/v1/events/${id}?scope=all`, { method: 'DELETE' });
      loc.route('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCancelling(false);
    }
  }

  if (error) return <div class="screen"><div class="error">{error}</div></div>;
  if (!event) return <div class="screen" style={{ textAlign: 'center' }}><span class="spinner" /></div>;

  return (
    <div class="screen">
      <h1>{event.title}</h1>
      <p class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <CalendarDays size={16} />
        {new Date(event.startsAt).toLocaleString('de-DE')} – {new Date(event.endsAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
      </p>
      {event.location ? (
        <p style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MapPin size={16} />
          {event.location}
        </p>
      ) : null}
      {event.recurrenceRule ? (
        <p class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Repeat size={14} />
          {event.recurrenceRule}
        </p>
      ) : null}
      {event.cancelledAt ? <p style={{ color: 'var(--c-danger)' }}>Abgesagt</p> : null}

      <h2 style={{ marginTop: 20 }}>Beteiligte</h2>
      <div class="stack">
        {event.participants.map((p) => {
          const role = ROLE_LABELS[p.role] ?? p.role;
          const color = actorColor(p);
          return (
            <div class="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {color ? <span class="person-color" style={{ background: color }} /> : null}
                <strong>{actorName(p)}</strong>
                <span class="muted" style={{ marginLeft: 'auto' }}>{role}</span>
              </div>
              <div class="muted" style={{ marginTop: 6, fontSize: 13 }}>
                <span class={`tag ${p.acceptStatus}`}>{STATUS_LABELS[p.acceptStatus] ?? p.acceptStatus}</span>
                {p.graphEventId ? <span class="muted" style={{ marginLeft: 8, fontSize: 12 }}>· in Outlook</span> : null}
              </div>
            </div>
          );
        })}
      </div>

      {!event.cancelledAt ? (
        <div class="stack" style={{ marginTop: 20 }}>
          <button onClick={() => loc.route(`/event/${id}/edit`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Pencil size={16} /> Bearbeiten
          </button>
          <button class="danger" disabled={cancelling} onClick={cancel} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {cancelling ? <span class="spinner" /> : <><Trash2 size={16} /> Termin absagen</>}
          </button>
        </div>
      ) : null}
    </div>
  );
}
